import type { OoxmlParagraphMeta } from "../types";
import {
  jcToTextAlign,
  lineSpacingToCss,
  parseTwips,
  twipsToPx,
} from "../units";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

function wAttr(el: Element, localName: string): string | null {
  return (
    el.getAttribute(`w:${localName}`) ??
    el.getAttributeNS(W_NS, localName) ??
    el.getAttribute(localName)
  );
}

function firstChildNS(parent: Element, localName: string): Element | null {
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (node instanceof Element && node.localName === localName) {
      return node;
    }
  }
  return null;
}

function readIndent(pPr: Element | null): Pick<
  OoxmlParagraphMeta,
  "marginLeftPx" | "marginRightPx" | "textIndentPx"
> {
  if (!pPr) return {};
  const ind = firstChildNS(pPr, "ind");
  if (!ind) return {};

  const startTwips =
    parseTwips(wAttr(ind, "start")) ?? parseTwips(wAttr(ind, "left"));
  const endTwips =
    parseTwips(wAttr(ind, "end")) ?? parseTwips(wAttr(ind, "right"));
  const firstLineTwips = parseTwips(wAttr(ind, "firstLine"));
  const hangingTwips = parseTwips(wAttr(ind, "hanging"));

  const result: Pick<
    OoxmlParagraphMeta,
    "marginLeftPx" | "marginRightPx" | "textIndentPx"
  > = {};

  if (endTwips != null) result.marginRightPx = twipsToPx(endTwips);

  if (hangingTwips != null) {
    const startPx = startTwips != null ? twipsToPx(startTwips) : 0;
    result.marginLeftPx = startPx;
    result.textIndentPx = -twipsToPx(hangingTwips);
  } else if (firstLineTwips != null) {
    if (startTwips != null) result.marginLeftPx = twipsToPx(startTwips);
    result.textIndentPx = twipsToPx(firstLineTwips);
  } else if (startTwips != null) {
    result.marginLeftPx = twipsToPx(startTwips);
  }

  return result;
}

function readSpacing(pPr: Element | null): Pick<
  OoxmlParagraphMeta,
  "marginTopPx" | "marginBottomPx" | "lineHeight"
> {
  if (!pPr) return {};
  const spacing = firstChildNS(pPr, "spacing");
  if (!spacing) return {};

  const result: Pick<
    OoxmlParagraphMeta,
    "marginTopPx" | "marginBottomPx" | "lineHeight"
  > = {};

  const before = parseTwips(wAttr(spacing, "before"));
  const after = parseTwips(wAttr(spacing, "after"));
  const line = wAttr(spacing, "line");
  const lineRule = wAttr(spacing, "lineRule");

  if (before != null) result.marginTopPx = twipsToPx(before);
  if (after != null) result.marginBottomPx = twipsToPx(after);

  const lh = lineSpacingToCss(line, lineRule);
  if (lh) result.lineHeight = lh;

  return result;
}

function readParagraphMeta(pEl: Element): OoxmlParagraphMeta {
  const pPr = firstChildNS(pEl, "pPr");
  const jcEl = pPr ? firstChildNS(pPr, "jc") : null;
  const alignment = jcToTextAlign(jcEl ? wAttr(jcEl, "val") : null);

  return {
    alignment,
    ...readIndent(pPr),
    ...readSpacing(pPr),
  };
}

/** Collect w:p elements in document order (depth-first, includes table cells). */
function collectParagraphElements(doc: Document | Element): Element[] {
  if (typeof doc.getElementsByTagNameNS === "function") {
    return Array.from(doc.getElementsByTagNameNS(W_NS, "p"));
  }
  return Array.from(doc.getElementsByTagName("w:p"));
}

/**
 * Extract paragraph-level properties from word/document.xml.
 * Supplements mammoth where it omits w:ind and w:spacing.
 */
export function parseDocumentParagraphMeta(xml: string): OoxmlParagraphMeta[] {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const parseError = doc.querySelector("parsererror");
  if (parseError) return [];

  return collectParagraphElements(doc).map(readParagraphMeta);
}

export async function extractParagraphMetaFromDocx(
  arrayBuffer: ArrayBuffer,
): Promise<OoxmlParagraphMeta[]> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(arrayBuffer);
  const xml = await zip.file("word/document.xml")?.async("text");
  if (!xml) return [];
  return parseDocumentParagraphMeta(xml);
}
